/**
 * GraphArtefactViewer Component
 *
 * Renders an interactive graph visualisation using D3.js force-directed layout.
 * Supports node colouring by label, edge labels, click-to-inspect,
 * zoom, pan, drag, and theme-aware styling.
 */

import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCenter,
  forceCollide,
  type SimulationNodeDatum,
  type SimulationLinkDatum,
} from "d3-force";
import { select } from "d3-selection";
import { zoom as d3Zoom, zoomIdentity } from "d3-zoom";
import { drag as d3Drag } from "d3-drag";
import { useTranslation } from "react-i18next";

import { Artefact } from "@/types/artefacts";
import { useTheme } from "@/hooks/use-theme";
import { logger } from "@/telemetry/logger";

import { Graph3DViewer } from "./Graph3DViewer";
import { type GraphData, type SelectedNodeInfo, buildLabelColourMap } from "./graphUtils";

/** Node radius for rendering and collision */
const NODE_RADIUS = 18;

interface GraphArtefactViewerProps {
  artefact: Artefact;
}

/** D3 simulation node — extends SimulationNodeDatum with our data. */
interface SimNode extends SimulationNodeDatum {
  id: string;
  label: string;
  name: string;
  properties: Record<string, string>;
  colour: string;
}

/** D3 simulation link — source/target resolve to SimNode after simulation starts. */
interface SimLink extends SimulationLinkDatum<SimNode> {
  id: string;
  label: string;
  properties: Record<string, string>;
}

/**
 * Read a CSS custom property from :root, falling back to the supplied default.
 */
function readCssVar(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();

  return value || fallback;
}

export const GraphArtefactViewer: React.FC<GraphArtefactViewerProps> = ({ artefact }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [selectedNode, setSelectedNode] = useState<SelectedNodeInfo | null>(null);
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [layoutReady, setLayoutReady] = useState(false);
  const [viewMode, setViewMode] = useState<"2d" | "3d">("2d");
  const { isDark } = useTheme();
  const { t } = useTranslation();

  // Parse the graph JSON content
  useEffect(() => {
    try {
      const parsed = JSON.parse(artefact.content) as GraphData;

      if (!parsed.graph?.nodes || !parsed.graph?.edges) {
        setParseError(t("graph.invalidData"));

        return;
      }
      setGraphData(parsed);
      setParseError(null);
    } catch {
      logger.warn("[GraphArtefactViewer] Failed to parse graph JSON");
      setParseError(t("graph.parseFailed"));
    }
  }, [artefact.content, t]);

  // Derive theme colours
  const themeColours = useMemo(() => {
    const surface = readCssVar("--content1", isDark ? "#111118" : "#f5f4f1");
    const foreground = readCssVar("--foreground", isDark ? "#e4e4e7" : "#27272a");
    const edgeColour = isDark ? "#52525b" : "#a1a1aa";
    const edgeLabelColour = isDark ? "#a1a1aa" : "#52525b";

    return { surface, foreground, edgeColour, edgeLabelColour };
  }, [isDark]);

  // Ref carrying the latest theme colours so the layout effect (which only
  // depends on graphData / viewMode) can read up-to-date colour values at
  // setup time without triggering a full simulation rebuild on theme toggle.
  const themeColoursRef = useRef(themeColours);
  const isDarkRef = useRef(isDark);

  useEffect(() => {
    themeColoursRef.current = themeColours;
    isDarkRef.current = isDark;
  }, [themeColours, isDark]);

  // Initialise D3 force simulation when graphData is available and viewMode is 2D.
  // Theme/colour changes are handled by a separate effect below (no simulation
  // rebuild on toggle).
  useEffect(() => {
    if (!graphData || !svgRef.current || !containerRef.current || viewMode !== "2d") return;

    setLayoutReady(false);

    const colourMap = buildLabelColourMap(graphData.graph.nodes);
    const width = containerRef.current.clientWidth || 800;
    const height = containerRef.current.clientHeight || 500;
    const initialColours = themeColoursRef.current;
    const initialIsDark = isDarkRef.current;

    // Build simulation data — deep copy to avoid mutating props
    const simNodes: SimNode[] = graphData.graph.nodes.map((n) => ({
      id: n.id,
      label: n.label,
      name: n.name || n.id,
      properties: n.properties,
      colour: colourMap[n.label] || "#999",
    }));

    const simLinks: SimLink[] = graphData.graph.edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      label: e.label,
      properties: e.properties,
    }));

    // Clear previous render
    const svg = select(svgRef.current);

    svg.selectAll("*").remove();
    svg.attr("width", width).attr("height", height);

    // Container group for zoom/pan
    const g = svg.append("g");

    // Zoom behaviour
    const zoomBehaviour = d3Zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.2, 4])
      .on("zoom", (event) => {
        g.attr("transform", event.transform);
      });

    svg.call(zoomBehaviour);

    // Click on background to dismiss tooltip
    svg.on("click", (event) => {
      if (event.target === svgRef.current) {
        setSelectedNode(null);
      }
    });

    // Arrow marker for directed edges
    svg
      .append("defs")
      .append("marker")
      .attr("id", "arrowhead")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", NODE_RADIUS + 10)
      .attr("refY", 0)
      .attr("markerWidth", 8)
      .attr("markerHeight", 8)
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M0,-4L8,0L0,4")
      .attr("fill", initialColours.edgeColour);

    // Create link elements
    const link = g
      .append("g")
      .attr("class", "links")
      .selectAll("line")
      .data(simLinks)
      .join("line")
      .attr("stroke", initialColours.edgeColour)
      .attr("stroke-width", 1.5)
      .attr("marker-end", "url(#arrowhead)");

    // Edge labels
    const edgeLabel = g
      .append("g")
      .attr("class", "edge-labels")
      .selectAll("text")
      .data(simLinks)
      .join("text")
      .text((d) => d.label)
      .attr("font-size", "11px")
      .attr("fill", initialColours.edgeLabelColour)
      .attr("text-anchor", "middle")
      .attr("dy", -6)
      .style("pointer-events", "none");

    // Create node groups
    const node = g
      .append("g")
      .attr("class", "nodes")
      .selectAll<SVGGElement, SimNode>("g")
      .data(simNodes)
      .join("g")
      .style("cursor", "pointer");

    // Node circles
    node
      .append("circle")
      .attr("r", NODE_RADIUS)
      .attr("fill", (d) => d.colour)
      .attr("stroke", initialIsDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)")
      .attr("stroke-width", 2);

    // Node labels
    node
      .append("text")
      .text((d) => d.name)
      .attr("font-size", "12px")
      .attr("fill", initialColours.foreground)
      .attr("text-anchor", "middle")
      .attr("dy", NODE_RADIUS + 14)
      .style("pointer-events", "none")
      .style("text-shadow", initialIsDark ? "0 1px 3px rgba(0,0,0,0.8)" : "none");

    // Node click handler
    node.on("click", (event, d) => {
      event.stopPropagation();
      const svgRect = svgRef.current?.getBoundingClientRect();

      if (!svgRect) return;
      setSelectedNode({
        name: d.name,
        label: d.label,
        properties: d.properties,
        x: event.clientX - svgRect.left,
        y: event.clientY - svgRect.top,
      });
    });

    // Drag behaviour
    const dragBehaviour = d3Drag<SVGGElement, SimNode>()
      .on("start", (event, d) => {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      })
      .on("drag", (event, d) => {
        d.fx = event.x;
        d.fy = event.y;
      })
      .on("end", (event, d) => {
        if (!event.active) simulation.alphaTarget(0);
        d.fx = null;
        d.fy = null;
      });

    node.call(dragBehaviour);

    // Force simulation
    const simulation = forceSimulation<SimNode>(simNodes)
      .force(
        "link",
        forceLink<SimNode, SimLink>(simLinks)
          .id((d) => d.id)
          .distance(140),
      )
      .force("charge", forceManyBody().strength(-400))
      .force("center", forceCenter(width / 2, height / 2))
      .force("collide", forceCollide(NODE_RADIUS + 20));

    simulation.on("tick", () => {
      link
        .attr("x1", (d) => (d.source as SimNode).x!)
        .attr("y1", (d) => (d.source as SimNode).y!)
        .attr("x2", (d) => (d.target as SimNode).x!)
        .attr("y2", (d) => (d.target as SimNode).y!);

      edgeLabel
        .attr("x", (d) => ((d.source as SimNode).x! + (d.target as SimNode).x!) / 2)
        .attr("y", (d) => ((d.source as SimNode).y! + (d.target as SimNode).y!) / 2);

      node.attr("transform", (d) => `translate(${d.x},${d.y})`);
    });

    simulation.on("end", () => {
      setLayoutReady(true);
      // Fit the graph into view after layout settles
      const gNode = g.node();

      if (gNode) {
        const bbox = gNode.getBBox();
        const padding = 40;
        const scaleX = width / (bbox.width + padding * 2);
        const scaleY = height / (bbox.height + padding * 2);
        const scale = Math.min(scaleX, scaleY, 1.5);
        const translateX = width / 2 - (bbox.x + bbox.width / 2) * scale;
        const translateY = height / 2 - (bbox.y + bbox.height / 2) * scale;

        svg.call(
          zoomBehaviour.transform,
          zoomIdentity.translate(translateX, translateY).scale(scale),
        );
      }
    });

    // Resize handling
    const resizeObserver = new ResizeObserver(() => {
      if (!containerRef.current) return;
      const newWidth = containerRef.current.clientWidth;
      const newHeight = containerRef.current.clientHeight;

      svg.attr("width", newWidth).attr("height", newHeight);
      simulation.force("center", forceCenter(newWidth / 2, newHeight / 2));
      simulation.alpha(0.1).restart();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      simulation.stop();
      resizeObserver.disconnect();
      svg.selectAll("*").remove();
    };
    // Theme/colour changes are reapplied by a separate effect below — do NOT
    // depend on themeColours / isDark here, or the whole simulation rebuilds
    // on every theme toggle and the layout resets.
  }, [graphData, viewMode]);

  // Reactive theme update — recolour existing D3 selections in place without
  // tearing down the simulation. Runs after the layout effect creates the SVG.
  useEffect(() => {
    if (!svgRef.current || viewMode !== "2d") return;
    const svg = select(svgRef.current);

    svg.select("defs marker#arrowhead path").attr("fill", themeColours.edgeColour);
    svg.selectAll<SVGLineElement, unknown>("g.links line").attr("stroke", themeColours.edgeColour);
    svg
      .selectAll<SVGTextElement, unknown>("g.edge-labels text")
      .attr("fill", themeColours.edgeLabelColour);
    svg
      .selectAll<SVGCircleElement, unknown>("g.nodes circle")
      .attr("stroke", isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)");
    svg
      .selectAll<SVGTextElement, unknown>("g.nodes text")
      .attr("fill", themeColours.foreground)
      .style("text-shadow", isDark ? "0 1px 3px rgba(0,0,0,0.8)" : "none");
  }, [themeColours, isDark, viewMode, graphData]);

  // Memoised values for legend
  const colourMap = useMemo(
    () => (graphData ? buildLabelColourMap(graphData.graph.nodes) : {}),
    [graphData],
  );
  const uniqueLabels = useMemo(
    () => (graphData ? Array.from(new Set(graphData.graph.nodes.map((n) => n.label))) : []),
    [graphData],
  );

  if (parseError) {
    return (
      <div className="flex items-center justify-center p-6 text-danger-500 text-sm">
        {parseError}
      </div>
    );
  }

  if (!graphData) {
    return (
      <div className="flex items-center justify-center p-6 text-default-500 text-sm">
        {t("graph.loading")}
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="relative rounded-lg border border-default-200 dark:border-default-700 overflow-hidden"
    >
      {/* Loading overlay while layout computes */}
      {!layoutReady && (
        <div
          aria-live="polite"
          className="absolute inset-0 z-30 flex items-center justify-center bg-default-100/80 dark:bg-default-900/80"
          role="status"
        >
          <span className="text-default-500 text-sm motion-safe:animate-pulse">
            {t("graph.computingLayout")}
          </span>
        </div>
      )}

      {/* Stats overlay + 2D/3D toggle */}
      <div className="absolute top-2 left-2 z-10 flex items-center gap-2 text-xs">
        <span className="bg-default-900/60 text-white px-2 py-1 rounded">
          {t("graph.stats", {
            nodeCount: graphData.graph.nodes.length,
            edgeCount: graphData.graph.edges.length,
          })}
        </span>
        <div className="flex bg-default-900/60 rounded overflow-hidden">
          <button
            aria-pressed={viewMode === "2d"}
            className={`px-2 py-1 text-xs transition-colors focus-visible:ring-2 focus-visible:ring-primary ${
              viewMode === "2d"
                ? "bg-white/20 text-white font-semibold"
                : "text-white/60 hover:text-white"
            }`}
            onClick={() => setViewMode("2d")}
          >
            2D
          </button>
          <button
            aria-pressed={viewMode === "3d"}
            className={`px-2 py-1 text-xs transition-colors focus-visible:ring-2 focus-visible:ring-primary ${
              viewMode === "3d"
                ? "bg-white/20 text-white font-semibold"
                : "text-white/60 hover:text-white"
            }`}
            onClick={() => setViewMode("3d")}
          >
            3D
          </button>
        </div>
      </div>

      {/* Legend */}
      {uniqueLabels.length > 1 && (
        <div className="absolute top-2 right-2 z-10 flex flex-wrap gap-1.5 max-w-[200px] justify-end">
          {uniqueLabels.map((label) => (
            <span
              key={label}
              className="flex items-center gap-1 bg-default-900/60 text-white px-2 py-0.5 rounded text-xs"
            >
              <span
                className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: colourMap[label] }}
              />
              {label}
            </span>
          ))}
        </div>
      )}

      {/* Graph renderer — 2D (D3 SVG) or 3D (Three.js) */}
      {viewMode === "2d" ? (
        <div
          ref={containerRef}
          style={{
            minHeight: "400px",
            height: "500px",
            background: themeColours.surface,
          }}
        >
          <svg
            ref={svgRef}
            aria-label={t("graph.stats", {
              nodeCount: graphData.graph.nodes.length,
              edgeCount: graphData.graph.edges.length,
            })}
            role="img"
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      ) : (
        <Graph3DViewer graphData={graphData} onNodeSelect={setSelectedNode} />
      )}

      {/* Node properties tooltip */}
      {selectedNode && (
        <div
          className="absolute z-20 rounded-lg p-3 max-w-[260px] bg-content1 text-content1-foreground border border-default-200 dark:border-default-700 shadow-xl"
          style={{
            left: Math.min(
              Math.max(selectedNode.x + 30, 10),
              (wrapperRef.current?.clientWidth || 400) - 270,
            ),
            top: Math.min(
              Math.max(selectedNode.y - 60, 10),
              (wrapperRef.current?.clientHeight || 500) - 160,
            ),
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <span
              className="inline-block w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: colourMap[selectedNode.label] }}
            />
            <span className="font-semibold text-sm truncate">{selectedNode.name}</span>
          </div>
          <span className="text-xs block mb-2 text-default-600 dark:text-default-400">
            {selectedNode.label}
          </span>
          {Object.keys(selectedNode.properties).length > 0 ? (
            <div className="space-y-1 max-h-[200px] overflow-y-auto">
              {Object.entries(selectedNode.properties).map(([key, value]) => (
                <div key={key} className="flex justify-between gap-2 text-xs">
                  <span className="flex-shrink-0 text-default-600 dark:text-default-400">
                    {key}
                  </span>
                  <span className="text-right truncate text-content1-foreground">
                    {String(value)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-xs text-default-600 dark:text-default-400">
              {t("graph.noProperties")}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
